/**
 * Operacao > Lembretes (spec 048). Depois de cada repasse alguem tem que zerar o saldo total no Monitor de Risco e
 * reinserir o perfil de risco da subconta, ate as 09:00 do proximo dia de pregao. Os dois passos sao feitos na Nelogica;
 * aqui fica quem marcou cada um e quando, e o que ainda falta.
 *
 * O desenho e o mock do Vitor (12/10), com tres mudancas dele: "Conta" e a subconta do plano, sem a coluna
 * Responsavel e sem os cartoes de numeros.
 */
import { Aviso, Botao, Campo, CampoCaixa, CampoSelecao, CampoTextoLongo, CabecalhoDePagina, Carregando, Destaque, Fatos, LinkInterno, Pilha, Secao, Selo, Tabela, Vazio, formatarDataHora, formatarDia, formatarReais, type Tom } from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { erroDe, useApi } from "../api";
import { usePagina } from "../pagina";

type Marca = { em: string; por: string | null } | null;
type Estado = "PENDENTE" | "PROGRAMADO" | "ATRASADO" | "CONCLUIDO";
type Lembrete = {
  id: string;
  estado: Estado;
  prazo: string;
  cliente: { id: string; nome: string; cpf: string };
  matricula_id: string;
  plano: string | null;
  subconta: string | null;
  monitor_zerado: Marca;
  perfil_reinserido: Marca;
  observacao: string | null;
  concluido: Marca;
  repasse: { id: string; valor_liquido_centavos: number; pago_em: string; registrado_em: string; pedido_id: string | null } | null;
};

const ESTADO: Record<Estado, { rotulo: string; tom: Tom }> = {
  PENDENTE: { rotulo: "Pendente", tom: "info" },
  PROGRAMADO: { rotulo: "Programado", tom: "neutro" },
  ATRASADO: { rotulo: "Atrasado", tom: "erro" },
  CONCLUIDO: { rotulo: "Concluído", tom: "sucesso" },
};

/** "13/10/2026 às 09:00" no fuso de Sao Paulo, como o resto do painel. */
const prazoEmTexto = (iso: string) => formatarDataHora(iso);

/** O que ainda falta fazer, em uma linha, pra coluna "Ação". */
function oQueFalta(l: Lembrete): string {
  if (l.concluido) return "Feito";
  const falta = [!l.monitor_zerado && "Zerar saldo no Monitor", !l.perfil_reinserido && "reinserir perfil de risco"].filter(Boolean) as string[];
  if (!falta.length) return "Concluir o lembrete";
  const frase = falta.join(" e ");
  return frase.charAt(0).toUpperCase() + frase.slice(1);
}

const quemEQuando = (m: Marca) => (m ? `${m.por ?? "Sistema"}, ${formatarDataHora(m.em)}` : undefined);

/** O painel lateral do mock: o prazo em destaque, os dois itens independentes, a observacao e concluir. */
function Detalhe({ l, aoFechar }: { l: Lembrete; aoFechar: () => void }) {
  const { postar } = useApi();
  const qc = useQueryClient();
  const [observacao, setObservacao] = useState(l.observacao ?? "");
  const [erro, setErro] = useState<string>();
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => { setObservacao(l.observacao ?? ""); setErro(undefined); }, [l.id]);
  const rodar = async (f: () => Promise<unknown>) => {
    setErro(undefined);
    setOcupado(true);
    try { await f(); await qc.invalidateQueries({ queryKey: ["lembretes"] }); await qc.invalidateQueries({ queryKey: ["avisos"] }); } catch (e) { setErro(erroDe(e)); setMarcado({ MONITOR: Boolean(l.monitor_zerado), PERFIL: Boolean(l.perfil_reinserido) }); } finally { setOcupado(false); }
  };
  // A caixa muda na hora do clique; o servidor confirma depois (e, se recusar, a caixa volta com o erro na tela).
  const [marcado, setMarcado] = useState({ MONITOR: Boolean(l.monitor_zerado), PERFIL: Boolean(l.perfil_reinserido) });
  useEffect(() => setMarcado({ MONITOR: Boolean(l.monitor_zerado), PERFIL: Boolean(l.perfil_reinserido) }), [l.id, l.monitor_zerado, l.perfil_reinserido]);
  const marcar = (item: "MONITOR" | "PERFIL", feito: boolean) => {
    setMarcado((m) => ({ ...m, [item]: feito }));
    void rodar(() => postar(`/v1/admin/lembretes/${l.id}/itens`, { item, feito })).then(() => undefined);
  };
  const concluido = Boolean(l.concluido);
  const prontos = Boolean(l.monitor_zerado && l.perfil_reinserido) && marcado.MONITOR && marcado.PERFIL;
  return (
    <Secao rotulo="Pós-repasse" titulo="Lembrete pós-repasse" acoes={<Botao tamanho="pequeno" variante="fantasma" onClick={aoFechar}>Fechar</Botao>}>
      <Fatos
        itens={[
          { rotulo: "Cliente", valor: l.cliente.nome, detalhe: l.cliente.cpf, mono: false },
          { rotulo: "Subconta", valor: l.subconta ?? "Sem subconta", detalhe: l.plano ?? undefined, mono: Boolean(l.subconta) },
          ...(l.repasse ? [{ rotulo: "Repasse relacionado", valor: formatarReais(l.repasse.valor_liquido_centavos), detalhe: `PIX de ${formatarDia(l.repasse.pago_em)}, registrado em ${formatarDataHora(l.repasse.registrado_em)}`, mono: true }] : []),
        ]}
      />
      {concluido ? (
        <Aviso tipo="sucesso">Concluído por {quemEQuando(l.concluido)}.{l.observacao ? ` Observação: ${l.observacao}` : ""}</Aviso>
      ) : (
        <Destaque tom={l.estado === "PROGRAMADO" ? "alerta" : "erro"} icone="relogio" rotulo={l.estado === "ATRASADO" ? "Prazo vencido" : "Prazo"} titulo={prazoEmTexto(l.prazo).replace(" às ", " antes das ")}>
          {l.estado === "ATRASADO" ? "Passou do prazo. Faça as ações abaixo agora." : "Faça as ações abaixo até o prazo."}
        </Destaque>
      )}
      <Pilha espaco={2}>
        <span className="mtc-rotulo">Checklist de ações</span>
        <CampoCaixa marcado={marcado.MONITOR} disabled={concluido} aoMudar={(v) => marcar("MONITOR", v)}>
          Zerar saldo total no Monitor de Risco{l.monitor_zerado && <span className="mtc-tabela__apoio">{quemEQuando(l.monitor_zerado)}</span>}
        </CampoCaixa>
        <CampoCaixa marcado={marcado.PERFIL} disabled={concluido} aoMudar={(v) => marcar("PERFIL", v)}>
          Adicionar novamente o perfil de risco{l.perfil_reinserido && <span className="mtc-tabela__apoio">{quemEQuando(l.perfil_reinserido)}</span>}
        </CampoCaixa>
      </Pilha>
      {!concluido && <CampoTextoLongo rotulo="Observações (opcional)" valor={observacao} aoMudar={setObservacao} maxLength={500} contador placeholder="Adicione observações sobre a execução deste lembrete" />}
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {!concluido && (
        <Botao disabled={!prontos || ocupado} carregando={ocupado} onClick={() => void rodar(() => postar(`/v1/admin/lembretes/${l.id}/concluir`, observacao.trim() ? { observacao: observacao.trim() } : {}))}>
          Concluir lembrete
        </Botao>
      )}
      {!concluido && !prontos && <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>Marque os dois itens pra concluir. Não importa a ordem.</span>}
      <LinkInterno className="mtc-link" href={l.repasse?.pedido_id ? `/solicitacoes/${l.repasse.pedido_id}` : `/matriculas/${l.matricula_id}`}>Ver repasse</LinkInterno>
    </Secao>
  );
}

export function PaginaLembretes() {
  const { ler } = useApi();
  const [params, setParams] = useSearchParams();
  const [busca, setBusca] = useState("");
  const [termo, setTermo] = useState("");
  const [estado, setEstado] = useState<Estado | "">("");
  const pag = usePagina("pagina", [termo, estado]);
  const aberto = params.get("id");
  useEffect(() => { const t = setTimeout(() => setTermo(busca.trim()), 250); return () => clearTimeout(t); }, [busca]);
  // Atualiza sozinha: o estado muda com o relogio (pendente vira atrasado as 09:00) e outra pessoa pode marcar.
  const q = useQuery({ queryKey: ["lembretes", termo], queryFn: () => ler<{ itens: Lembrete[] }>(`/v1/admin/lembretes${termo.length >= 2 ? `?busca=${encodeURIComponent(termo)}` : ""}`), refetchInterval: 60_000 });
  const todos = q.data?.itens ?? [];
  const linhas = estado ? todos.filter((l) => l.estado === estado) : todos;
  const hoje = todos.filter((l) => l.estado === "PENDENTE").length;
  const atrasados = todos.filter((l) => l.estado === "ATRASADO").length;
  const selecionado = todos.find((l) => l.id === aberto) ?? null;
  const abrir = (id: string | null) => { const p = new URLSearchParams(params); if (id) p.set("id", id); else p.delete("id"); setParams(p, { replace: true }); };
  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina titulo="Lembretes" descricao="Ações operacionais geradas depois de cada repasse: zerar o saldo total no Monitor de Risco e reinserir o perfil de risco até as 09:00 do próximo dia de pregão." />
      {atrasados > 0 && (
        <Destaque icone="alerta" rotulo="Atrasados" titulo={`${atrasados} ${atrasados === 1 ? "lembrete passou" : "lembretes passaram"} do prazo`}>
          <button type="button" className="mtc-link" onClick={() => setEstado("ATRASADO")}>Ver atrasados</button>
        </Destaque>
      )}
      {hoje > 0 && (
        <Destaque tom="alerta" icone="relogio" rotulo="Hoje" titulo={`${hoje} ${hoje === 1 ? "ação pós-repasse precisa" : "ações pós-repasse precisam"} ser concluída${hoje === 1 ? "" : "s"} antes das 09:00`}>
          <button type="button" className="mtc-link" onClick={() => setEstado("PENDENTE")}>Ver pendentes</button>
        </Destaque>
      )}
      <div className="mtc-linha" style={{ gap: "var(--e-3)", flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ flex: "1 1 280px" }}><Campo rotulo="Buscar" valor={busca} aoMudar={setBusca} placeholder="Cliente, CPF ou subconta" /></div>
        <div style={{ flex: "0 1 220px" }}>
          <CampoSelecao rotulo="Status" valor={estado} aoMudar={(v) => setEstado(v as Estado)} vazio="Todos os status" opcoes={(Object.keys(ESTADO) as Estado[]).map((e) => ({ valor: e, rotulo: ESTADO[e].rotulo }))} />
        </div>
        {(busca || estado) && <button type="button" className="mtc-link" style={{ paddingBottom: 12 }} onClick={() => { setBusca(""); setEstado(""); }}>Limpar filtros</button>}
      </div>
      {q.isError && <Aviso tipo="erro">{erroDe(q.error)}</Aviso>}
      <div className="mtc-grade" style={{ gridTemplateColumns: selecionado ? "repeat(auto-fit, minmax(min(100%, 380px), 1fr))" : "minmax(0, 1fr)", alignItems: "start" }}>
        {q.isLoading ? <Carregando compacto texto="Buscando os lembretes" /> : (
          <Tabela
            paginacao={pag}
            linhas={linhas}
            chave={(l) => l.id}
            aoClicar={(l) => abrir(l.id)}
            destaque={(l) => l.id === aberto}
            vazio={<Vazio titulo={estado || termo ? "Nada com esse filtro" : "Nenhum lembrete aberto"}>{estado || termo ? "Troque o filtro ou limpe a busca." : "Cada repasse registrado cria um lembrete aqui, com prazo até as 09:00 do próximo dia de pregão."}</Vazio>}
            colunas={[
              { chave: "cliente", rotulo: "Cliente", celula: (l) => <><strong>{l.cliente.nome}</strong><span className="mtc-tabela__apoio mtc-mono">{l.cliente.cpf}</span></> },
              { chave: "conta", rotulo: "Conta", celula: (l) => <><span className="mtc-mono">{l.subconta ?? "Sem subconta"}</span>{l.plano && <span className="mtc-tabela__apoio">{l.plano}</span>}</> },
              { chave: "tipo", rotulo: "Tipo", celula: () => <Selo tom="sucesso">Pós-repasse</Selo> },
              { chave: "acao", rotulo: "Ação", celula: (l) => oQueFalta(l) },
              { chave: "prazo", rotulo: "Prazo", celula: (l) => <span className="mtc-mono">{prazoEmTexto(l.prazo)}</span> },
              { chave: "status", rotulo: "Status", celula: (l) => <Selo tom={ESTADO[l.estado].tom}>{ESTADO[l.estado].rotulo}</Selo> },
            ]}
          />
        )}
        {selecionado && <Detalhe l={selecionado} aoFechar={() => abrir(null)} />}
      </div>
      {aberto && !selecionado && !q.isLoading && <Aviso tipo="info">Esse lembrete não está mais na lista (concluído há mais de 7 dias ou o repasse foi apagado).</Aviso>}
    </Pilha>
  );
}
